import { Component, ViewChild, ViewChildren, OnInit, OnDestroy, QueryList, ChangeDetectorRef, ChangeDetectionStrategy, forwardRef, ElementRef } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Store } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import * as fromStore from '../store/reducers';
import { ModuleConfiguration } from '../vocabulary.module.config';
import { CodeSystemVersionDashboardModel } from '../models/CodeSystemVersionDashboardModel'
import { ValueSetGroupModel } from '../models/ValueSetGroupModel';
import { ValueSetModel } from '../models/ValueSetModel';
import { SearchComponent } from '../../pillar/search/components/search.component';
import * as vocabularyActions from '../store/actions';
import { MatExpansionPanel, MatDialog, MatCheckbox, MAT_DIALOG_DATA } from '@angular/material';
import { DeleteCodeSystemConfirmationDialogComponent } from './delete-code-system-confirmation-dialog.component';
import { TileContainer } from '../../pillar/dashboard/services/TileContainer';
import { TileComponent } from '../../pillar/dashboard/components/tile.component';
import { AddValueSetDialogComponent } from './add-value-set-dialog.component';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import { CurrentUserService } from '../../security/services/CurrentUserService';
import { CodeSystemVersionViolationDialogComponent } from './code-system-version-violation-dialog.component';
import { AppConfigurationService } from '../../core/services/AppConfigurationService';


@Component({
  selector: 'brd-vocabulary-dashboard',
  providers: [{ provide: TileContainer, useExisting: forwardRef(() => VocabularyDashboardComponent) }],
  templateUrl: './vocabulary-dashboard.component.html',
  styleUrls: ['./vocabulary-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VocabularyDashboardComponent extends TileContainer implements OnInit, OnDestroy {
  static dashboardName = 'vocabulary';
  displayedColumns = ['conceptName' ];
  key: string;
  loading: boolean;
  errors$: Observable<string[]>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  valueSetGroups: ValueSetGroupModel[];
  valueSets: ValueSetModel[];
  vocabularyVersion: string;
  vocabularyName: string;
  valueSetSelected: boolean = false;
  isDraft: boolean;
  canCreateDraft: boolean;
  conceptName: string;
  conceptCode: string;
  canAddValueSet:boolean;
  valueSetsTileComponent: TileComponent;
  panels: QueryList<MatExpansionPanel>;
  editPermission = 'VocabularyModule.CodeSystem:Edit';
  EditBaseVocabularyPermission = 'VocabularyModule.CodeSystem:EditBase';
  selectedValueSet_CodeKey = 'VocabularyModule_SelectedValueSetCode';
  selectedValueSet_NameKey = 'VocabularyModule_SelectedValueSetName';
  errors: string[] = [];
  hideEmpty: boolean = true;

  @ViewChild('valueSetsTile')
  valueSetsTile: ElementRef;

  @ViewChild(SearchComponent)
  search: SearchComponent;

  @ViewChildren(MatExpansionPanel)
  set viewPanels(panels: QueryList<MatExpansionPanel>) {
    this.panels = panels;
    if (panels) {
      panels.changes
        .takeUntil(this.destroyed$)
        .subscribe(() => {
          if (this.search.searchControl.value) {
            panels.forEach(p => p.open());
          } else {
            panels.forEach(p => p.close());
          }
        });
    }
  }

  constructor(private currentUserService: CurrentUserService,
              private router: Router,
              private store: Store<any>,
              private actions$: Actions,
              private route: ActivatedRoute,
              private changeDetectorRef: ChangeDetectorRef,
              private dialog: MatDialog,
              private appConfigurationService: AppConfigurationService) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.route.params.take(1).subscribe(p => {
      this.key = p['key'];
      this.getData();
    });

    this.search
      .searchControl
      .valueChanges
      .debounceTime(500)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.getData();
        if (!this.search.searchControl.value) {
          this.panels.forEach(p => p.close());
        }
      });

    this.store.select(fromStore.getCodeSystemVersionSelection)
      .takeUntil(this.destroyed$)
      .subscribe((v: CodeSystemVersionDashboardModel) => {
        if (v) {
          this.valueSetGroups = v.valueSetGroups;
          this.vocabularyName = v.vocabularyName;
          this.vocabularyVersion = v.vocabularyVersion;
          this.isDraft = v.isDraft;
          this.canAddValueSet=v.canAddValueSet;
          this.canCreateDraft = v.canCreateDraft;
          this.loading = false;
          this.changeDetectorRef.markForCheck();
          this.checkSelected();
        }
      });

    this.actions$.ofType(vocabularyActions.CREATE_CODESYSTEMVERSION_DRAFT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: vocabularyActions.CreateCodeSystemVersionDraftSuccessAction) => {
        this.loading = true;
        this.router.navigateByUrl('/configuration');
      });

    this.actions$.ofType(dashboardActions.getTileErrorsType<CodeSystemVersionDashboardModel>(ModuleConfiguration.moduleName, CodeSystemVersionDashboardModel))
      .takeUntil(this.destroyed$)
      .subscribe((a: dashboardActions.TileErrorsAction<CodeSystemVersionDashboardModel>) => {
        this.errors = a.payload.errors;
        this.loading = false;
      });

    this.actions$.ofType(vocabularyActions.FINALIZE_CODESYSTEMVERSION_DRAFT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: vocabularyActions.FinalizeCodeSystemVersionDraftSuccessAction) => {
        if (a.payload.success) {
          this.loading = true;
          this.getData();
          this.errors = [];
        } else {
          this.loading = false;
        }
        this.changeDetectorRef.markForCheck();
      });

    this.valueSetsTileComponent = new TileComponent(this.valueSetsTile);
    this.tileComponents.push(this.valueSetsTileComponent);
  }

  selectItem(item:ValueSetModel) {
    this.valueSetSelected = true;
    this.conceptName = item.conceptName;
    this.conceptCode = item.conceptCode;
    localStorage.setItem(this.selectedValueSet_CodeKey, this.conceptCode);
    localStorage.setItem(this.selectedValueSet_NameKey, this.conceptName);
  }

  checkSelected() {
    var code = localStorage.getItem(this.selectedValueSet_CodeKey);
    var name = localStorage.getItem(this.selectedValueSet_NameKey);
    if (code && name) {
      this.valueSetSelected = true;
      this.conceptName = name;
      this.conceptCode = code;
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  back() {
    this.router.navigateByUrl('/configuration');
  }

  getData() {
    this.hideEmpty = !this.hideEmpty;
    this.store.dispatch(new vocabularyActions.GetValueSetListAction(ModuleConfiguration.moduleName, CodeSystemVersionDashboardModel, { key: this.key, searchTerm: this.search.searchControl.value, hideEmpty: !this.hideEmpty }));
  }

  createDraft() {
    this.loading = true;
    this.store.dispatch(new vocabularyActions.CreateCodeSystemVersionDraftAction({ key: this.key }));
  }

  finalizeDraft() {
    this.loading = true;
    this.store.dispatch(new vocabularyActions.FinalizeCodeSystemVersionDraftAction({ key: this.key }));
  }

  deleteDraft() {
    this.dialog.open(DeleteCodeSystemConfirmationDialogComponent,
      {
        panelClass: 'customized-dialog',
        data:
        {
            key: this.key,
            vocabularyName: this.vocabularyName
        }
      });
  }

  showAdd(): void {
    this.dialog.open(AddValueSetDialogComponent,
      {
        panelClass: 'customized-dialog',
        data:
        {
          name: '',
          codeSystemVersionKey: this.key,
          search: this.search.searchControl.value,
          hideEmpty:this.hideEmpty
        }
      });
  }

  calculateErrorNumber(): string {
    var number = this.errors.length;
    if (number > 99) {
      return "99+";
    }
    return number.toString();
  }

  showErrors(): void {
    this.dialog.open(CodeSystemVersionViolationDialogComponent,
    {
      panelClass: 'customized-dialog',
      data: {
        errors: this.errors
      }
    });
  }

  hasAccessToCodeSystem(): boolean {
    var baseCodeSystems = this.appConfigurationService.configuration.baseCodeSystems.toLowerCase().split(",").map(Function.prototype.call, String.prototype.trim);
    return !baseCodeSystems.includes(this.vocabularyName.toLowerCase()) || (baseCodeSystems.includes(this.vocabularyName.toLowerCase()) && this.currentUserService.hasPermission(this.EditBaseVocabularyPermission));
  }
}





// WEBPACK FOOTER //
// ./src/app/vocabulary/components/vocabulary-dashboard.component.ts